import { queryOptions, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format, subDays } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { getNicaSlotsForDate } from "@/lib/nica-schedule";

// Always use Costa Rica timezone for consistency
const COSTA_RICA_TIMEZONE = "America/Costa_Rica";

interface LotteryResult {
  time: string;
  number: string;
  special: string;
  third: string;
  isPlayed: boolean;
}

interface DayResults {
  date: string;
  nicaraguaResults: LotteryResult[];
  costaRicaResults: LotteryResult[];
}

// Get current date in Costa Rica timezone
const getCostaRicaDate = (): Date => {
  return toZonedTime(new Date(), COSTA_RICA_TIMEZONE);
};

const buildNicaResults = (date: Date, row: any): LotteryResult[] =>
  getNicaSlotsForDate(date).map((slot) => {
    const raw = row[slot.dbCol] as any;
    return {
      time: slot.time,
      number: raw?.number ?? "XX",
      special: raw?.special ?? "XX",
      third: raw?.third ?? "XX",
      isPlayed: raw?.number !== "XX" && raw?.number !== undefined,
    };
  });

const fetchResultsForDate = async (date: Date): Promise<DayResults | null> => {
  const dateStr = format(date, "yyyy-MM-dd");
  
  if (import.meta.env.DEV) console.log("Fetching lottery results for date:", dateStr);
  
  const { data, error } = await supabase
    .from("lottery_results")
    .select("date,nica_11am,nica_3pm,nica_6pm,nica_9pm,tica_11am,tica_3pm,tica_9pm")
    .eq("date", dateStr)
    .maybeSingle();

  if (error) {
    if (import.meta.env.DEV) console.error("Error fetching lottery results:", error);
    throw error;
  }

  if (import.meta.env.DEV) console.log("Lottery results data:", data);

  // Public reads never initialize rows or wait on an admin session.
  if (!data) return null;

  const results = {
    date: dateStr,
    nicaraguaResults: buildNicaResults(date, data),
    costaRicaResults: [
      { time: "1:00 PM", ...(data.tica_11am as any), isPlayed: (data.tica_11am as any).number !== "XX" },
      { time: "4:30 PM", ...(data.tica_3pm as any), isPlayed: (data.tica_3pm as any).number !== "XX" },
      { time: "7:30 PM", ...(data.tica_9pm as any), isPlayed: (data.tica_9pm as any).number !== "XX" },
    ],
  };
  
  if (import.meta.env.DEV) console.log("Processed results:", results);
  return results;
};

export type { DayResults, LotteryResult };

export const lotteryResultsQuery = (daysAgo: number = 0) => {
  const date = subDays(getCostaRicaDate(), daysAgo);
  return queryOptions({
    queryKey: ["lottery-results", format(date, "yyyy-MM-dd")],
    queryFn: () => fetchResultsForDate(date),
    staleTime: 30_000,
    retry: 1,
  });
};

export const useLotteryResults = (daysAgo: number = 0) =>
  useQuery(lotteryResultsQuery(daysAgo));

export const lotteryResultsByDateQuery = (date: Date) =>
  queryOptions({
    queryKey: ["lottery-results", format(date, "yyyy-MM-dd")],
    queryFn: () => fetchResultsForDate(date),
    staleTime: 30_000,
    retry: 1,
  });

export const useLotteryResultsByDate = (date: Date) => {
  return useQuery(lotteryResultsByDateQuery(date));
};
